import type { MessageKey } from '@freightapi/i18n'
import { Button, EmptyState, Spinner } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { DriverShipmentCard } from '../components/driver/DriverShipmentCard'
import { DriverSignIn } from '../components/driver/DriverSignIn'
import { DriverTourSection } from '../components/driver/DriverTourSection'
import { ApiError } from '../lib/api'
import {
  type DriverSession,
  type DriverShipment,
  type DriverShipmentList,
  endDriverSession,
  loadDriverShipments,
  looseShipments,
  requestDriverOtp,
  verifyDriverOtp,
} from '../lib/driver'
import { clearDriverSession, readDriverSession, storeDriverSession } from '../lib/driver-session'
import { useSignI18n } from '../lib/locale'

/**
 * The driver's own list of trips, reached from a session instead of a link (M2-07).
 *
 * Every other screen in this app is opened from a token in a URL. This one is not: a driver has no
 * link, only the phone number their planner wrote on the consignment note. So it signs in with
 * that number and a texted code, keeps the session for twelve hours, and shows what is open today
 * with a deep link into the flow each shipment's status calls for.
 *
 * A 401 anywhere — expired, signed out, or signed in again on another phone — drops straight back
 * to the sign-in screen with the number prefilled, because there is nothing a driver can do about
 * a session but start a new one.
 */
export const Route = createFileRoute('/driver')({ component: DriverScreen })

type DriverState =
  | { readonly kind: 'signed_out'; readonly phone?: string; readonly error?: string }
  | { readonly kind: 'loading'; readonly session: DriverSession }
  | {
      readonly kind: 'ready'
      readonly session: DriverSession
      readonly list: DriverShipmentList
    }
  | {
      readonly kind: 'error'
      readonly session: DriverSession
      readonly messageKey: MessageKey<'sign'>
    }

function DriverScreen() {
  const { t, locale } = useSignI18n()
  const [state, setState] = useState<DriverState>(() => {
    const session = readDriverSession()
    return session === undefined ? { kind: 'signed_out' } : { kind: 'loading', session }
  })

  // Only the `Accept-Language` of the next request, never a reason to fetch again.
  const localeRef = useRef(locale)
  localeRef.current = locale

  /** Signing out locally is the answer to every 401; the server has already forgotten the token. */
  const signedOut = useCallback((phone?: string, error?: string) => {
    clearDriverSession()
    setState({
      kind: 'signed_out',
      ...(phone === undefined ? {} : { phone }),
      ...(error === undefined ? {} : { error }),
    })
  }, [])

  /** What a driver can read off a failed call; anything technical becomes the generic message. */
  const messageFor = useCallback(
    (error: unknown, fallback: MessageKey<'sign'>): string => {
      if (!(error instanceof ApiError)) return t('error.unexpected')
      if (error.status === 0) return t('error.network')
      if (error.status === 429) return t('driver.error.locked')
      return t(fallback)
    },
    [t],
  )

  useEffect(() => {
    if (state.kind !== 'loading') return
    const controller = new AbortController()
    const session = state.session

    loadDriverShipments(session.token, { locale: localeRef.current, signal: controller.signal })
      .then((list) => {
        if (!controller.signal.aborted) setState({ kind: 'ready', session, list })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        if (error instanceof ApiError && error.status === 401) {
          signedOut(session.phone, t('driver.error.session_expired'))
          return
        }
        setState({
          kind: 'error',
          session,
          messageKey: error instanceof ApiError ? error.messageKey : 'error.unexpected',
        })
      })

    return () => controller.abort()
  }, [state, signedOut, t])

  const onRequestCode = useCallback(
    async (phone: string): Promise<string | undefined> => {
      try {
        await requestDriverOtp(phone, { locale: localeRef.current })
        return undefined
      } catch (error) {
        return messageFor(error, 'error.unexpected')
      }
    },
    [messageFor],
  )

  const onVerify = useCallback(
    async (phone: string, code: string): Promise<string | undefined> => {
      try {
        const session = await verifyDriverOtp(phone, code, { locale: localeRef.current })
        storeDriverSession(session)
        setState({ kind: 'loading', session })
        return undefined
      } catch (error) {
        return messageFor(error, 'driver.error.code_invalid')
      }
    },
    [messageFor],
  )

  const onSignOut = useCallback(
    async (session: DriverSession) => {
      // Best effort: the token leaves this device either way, so a lost connection still signs out.
      try {
        await endDriverSession(session.token, { locale: localeRef.current })
      } catch {
        // Nothing to report; the session is gone from here regardless.
      }
      signedOut(session.phone)
    },
    [signedOut],
  )

  if (state.kind === 'signed_out') {
    return (
      <DriverSignIn
        onRequestCode={onRequestCode}
        onVerify={onVerify}
        {...(state.phone === undefined ? {} : { initialPhone: state.phone })}
        {...(state.error === undefined ? {} : { initialError: state.error })}
      />
    )
  }

  if (state.kind === 'loading') {
    return (
      <div className="flex justify-center py-10" data-driver-step="loading">
        <Spinner label={t('loading')} size="lg" />
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <EmptyState
        title={t('error.title')}
        description={t(state.messageKey)}
        action={
          <Button onClick={() => setState({ kind: 'loading', session: state.session })}>
            {t('button.retry')}
          </Button>
        }
      />
    )
  }

  const reload = () => setState({ kind: 'loading', session: state.session })

  return (
    <section
      className="flex flex-col gap-4"
      data-driver-overview={state.list.data.length}
      data-driver-tours={state.list.tours.length}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <h1 className="font-semibold text-display text-ink">{t('driver.list.title')}</h1>
          <p className="text-caption text-muted">{state.list.driver.phone}</p>
        </div>
        <Button variant="ghost" onClick={() => void onSignOut(state.session)}>
          {t('driver.button.sign_out')}
        </Button>
      </header>

      {/*
        A sandbox list holds rehearsals, and the API now says so per session (issue #168). Saying
        it on the screen as well is the point: the rows underneath look exactly like real work,
        down to the button that signs.
      */}
      {state.list.driver.mode === 'test' ? (
        <div
          data-driver-mode="test"
          role="status"
          className="flex flex-col gap-0.5 rounded-card border border-warn/50 bg-warn/15 p-3"
        >
          <span className="font-medium text-caption text-ink">{t('driver.sandbox.title')}</span>
          <span className="text-caption text-ink">{t('driver.sandbox.body')}</span>
        </div>
      ) : null}

      {state.list.data.length === 0 ? (
        <EmptyState
          title={t('driver.empty.title')}
          description={t('driver.empty.body')}
          action={<Button onClick={reload}>{t('driver.button.refresh')}</Button>}
        />
      ) : (
        <>
          {state.list.tours.map((tour) => (
            <DriverTourSection key={tour.id} tour={tour} shipments={byId(state.list.data)} />
          ))}

          {/*
            What is left over. The heading only appears next to a tour: a driver with nothing but
            single rides is looking at the screen M2-07 built, and does not need to be told that
            his whole day is "the rest".
          */}
          {looseShipments(state.list).length === 0 ? null : (
            <>
              {state.list.tours.length === 0 ? null : (
                <h2 className="font-semibold text-ink text-lead">{t('driver.tour.loose')}</h2>
              )}
              <ul className="flex list-none flex-col gap-3 p-0">
                {looseShipments(state.list).map((shipment) => (
                  <DriverShipmentCard key={shipment.id} shipment={shipment} />
                ))}
              </ul>
            </>
          )}

          <Button variant="secondary" block onClick={reload}>
            {t('driver.button.refresh')}
          </Button>
        </>
      )}
    </section>
  )
}

function byId(shipments: DriverShipmentList['data']): ReadonlyMap<string, DriverShipment> {
  return new Map(shipments.map((shipment) => [shipment.id, shipment]))
}
